<template>
  <div class="step-phone">
    <form
      class="form"
      @invalid.capture.prevent="handleInvalid"
      @submit.prevent="handleSubmit"
      @change="handleChange"
    >
      <fieldset class="fieldset">
        <legend class="legend">
          Verify your phone number
        </legend>
        <Phone
          id="phone"
          v-model="phone"
          label="Phone Number"
          required
          :error="formErrors.phone"
          error-code="UserPhoneNumberInvalid"
          aria-required="true"
        />
      </fieldset>
      <div
        v-if="
          !status.confirmed.success &&
            !status.requested.codeSent &&
            !status.requested.sendingCode
        "
        class="navigation-buttons navigation-buttons--phone-verification"
      >
        <button
          type="submit"
          aria-label="Text Verify"
          class="button button--submit"
          :disabled="status.requested.sendingCode"
        >
          Text verify
        </button>
        <button
          type="submit"
          class="button button--submit"
          aria-label="Voice Verify"
          :disabled="status.requested.sendingCode"
          @click="requestVerificationCode('call')"
        >
          Voice verify
        </button>
      </div>
    </form>
    <transition name="slide-down">
      <form
        v-show="
          !status.confirmed.success &&
            (status.requested.codeSent || status.requested.sendingCode)
        "
        ref="verification"
        class="form"
        @invalid.capture.prevent="handleInvalid"
        @submit.prevent="handleVerificationSubmit"
        @change="handleChange"
      >
        <Input
          id="verification"
          v-model="verification"
          class="input--verification-code"
          label="Verification Code"
          pattern="[0-9]*"
          inputmode="numeric"
          mask="###-########"
          required
          :loading="status.confirmed.loading"
          :error="errorMessage"
          error-code="UserPhoneNumberInvalid"
          aria-required="true"
        />
        <transition name="slide-down">
          <p
            v-if="!status.requested.sendingCode"
            class="form__note"
          >
            <button
              class="button-link"
              @click="requestVerificationCode('sms')"
            >
              Text me a code
            </button>
            &nbsp;
            <button
              class="button-link"
              @click="requestVerificationCode('call')"
            >
              Call me for code
            </button>
          </p>
        </transition>
      </form>
    </transition>
    <div class="form">
      <p
        v-if="canSkipVerification"
        class="form__success"
      >
        Verification code is not required. Please click Continue to sign up.
      </p>
      <div class="navigation-buttons">
        <button
          class="button button--submit"
          :disabled="
            !status.confirmed.success && (!isFilled || status.confirmed.loading)
          "
          @click="handleVerificationSubmit"
        >
          Continue
        </button>
        <PreviousStepButton />
      </div>
    </div>
  </div>
</template>
<script>
import Phone from '../components/Phone.vue';
import Input from '../components/Input.vue';
import PreviousStepButton from '../components/PreviousStepButton.vue';

const VERIFICATION_CODE_LENGTH = 6;

export default {
  components: {
    Input,
    Phone,
    PreviousStepButton,
  },
  data() {
    const { user } = this.$store.state;
    return {
      showPassword: false,
      phone: user.phone,
      verification: '',
      formErrors: {},
    };
  },
  computed: {
    registration() {
      return this.$store.state.registration;
    },
    status() {
      return this.$store.state.phoneVerification;
    },
    canSkipVerification() {
      return this.status.confirmed.canSkipVerification;
    },
    passType() {
      return this.$store.getters.passType;
    },
    isFilled() {
      return (
        (this.phone !== '' && this.verification !== '')
      );
    },
    errorMessage() {
      if (this.status.confirmed.error) {
        if (this.status.confirmed.error === 'INVALID_CODE') {
          return 'Invalid code. Please try again.';
        }
      }
      if (this.status.requested.error) {
        if (this.status.requested.error === 'UNABLE_TO_SEND') {
          return 'Unable to send verification code. Please try again or contact Customer Service for assistance.';
        }
      }
      return null;
    },
  },
  watch: {
    status: {
      handler() {
        if (this.status.confirmed.error === 'INVALID_NUMBER') {
          this.formErrors = {
            ...this.formErrors,
            phone: 'Unable to validate phone number. Please try again or contact Customer Service for assistance.',
          };
        }
        if (this.status.confirmed.error === 'VOIP_NOT_ALLOWED') {
          this.formErrors = {
            ...this.formErrors,
            phone: "Sorry, we don't accept virtual phone numbers (VoIP, such as Google Voice). Please provide a valid, non-virtual phone number to continue.",
          };
        }
      },
      deep: true,
    },
    phone() {
      this.$store.dispatch('cancelPhoneNumberVerification', {
        cancelRequested: true,
      });
      this.verification = '';
    },
    verification() {
      this.$store.dispatch('cancelPhoneNumberVerification');
      if (this.verification.length === VERIFICATION_CODE_LENGTH) {
        this.handleVerificationSubmit();
      }
    },
  },
  methods: {
    handleInvalid(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.id]: e.target.validationMessage,
      };
    },
    handleChange(e) {
      this.formErrors = {
        ...this.formErrors,
        [e.target.id]: '',
      };
    },
    async requestVerificationCode(method = 'sms') {
      this.$store.dispatch('requestPhoneVerification', {
        type: method,
        phone: this.phone,
        language: this.getSelectedLanguage(),
      });
      this.$nextTick(() => {
        this.$refs.verification.querySelector('input').focus();
      });
    },
    handleSubmit() {
      if (this.status.confirmed.success) {
        this.continue();
        return;
      }
      this.requestVerificationCode('sms');
    },
    async handleVerificationSubmit() {
      if (this.status.confirmed.success) {
        this.continue();
        return;
      }
      await this.$store.dispatch('verifyPhoneNumber', {
        code: this.verification,
        phone: this.phone,
      });

      if (this.status.confirmed.success) {
        this.continue();
      }
    },
    continue() {
      this.$router.push({
        name: this.$store.getters.nextStep(this.$route.name),
      });
    },
    getSelectedLanguage() {
      const langEl = document.querySelector('html[lang]');
      if (langEl) {
        return langEl.getAttribute('lang');
      }
      return 'en';
    },
  },
};
</script>
